// ================================================================
// PROPRIETARY & CONFIDENTIAL — Miskulin Technologies Inc.
// Copyright (c) 2026 Miskulin Technologies. All rights reserved.
// ================================================================
import React, { useState } from 'react';
import { useAuth } from '../AuthContext';

const PLATFORMS = [
  { id: 'facebook', name: 'Facebook / Meta', icon: '\uD83D\uDCD8', formats: ['Carousel', 'Single Image', 'Video', 'Stories', 'Reels'], dims: '1080x1080 / 1200x628', charLimit: 125 },
  { id: 'google', name: 'Google Ads', icon: '\uD83D\uDD0D', formats: ['Search', 'Display', 'Responsive', 'Shopping'], dims: '300x250 / 728x90 / 160x600', charLimit: 90 },
  { id: 'instagram', name: 'Instagram', icon: '\uD83D\uDCF7', formats: ['Feed', 'Stories', 'Reels', 'Carousel'], dims: '1080x1080 / 1080x1920', charLimit: 125 },
  { id: 'linkedin', name: 'LinkedIn', icon: '\uD83D\uDCBC', formats: ['Sponsored Content', 'Message Ads', 'Text Ads'], dims: '1200x627', charLimit: 150 },
  { id: 'tiktok', name: 'TikTok', icon: '\uD83C\uDFB5', formats: ['In-Feed Video', 'TopView', 'Spark Ads'], dims: '1080x1920', charLimit: 100 },
  { id: 'youtube', name: 'YouTube', icon: '\u25B6\uFE0F', formats: ['Skippable', 'Non-Skippable', 'Bumper', 'Display'], dims: '1920x1080 / 300x250', charLimit: 70 },
  { id: 'twitter', name: 'X / Twitter', icon: '\uD83D\uDCAC', formats: ['Promoted Tweet', 'Video', 'Carousel'], dims: '1200x675 / 800x418', charLimit: 280 },
  { id: 'pinterest', name: 'Pinterest', icon: '\uD83D\uDCCC', formats: ['Standard Pin', 'Video Pin', 'Carousel', 'Shopping'], dims: '1000x1500', charLimit: 500 },
  { id: 'snapchat', name: 'Snapchat', icon: '\uD83D\uDC7B', formats: ['Snap Ads', 'Story Ads', 'Collection', 'Filters'], dims: '1080x1920', charLimit: 34 },
  { id: 'reddit', name: 'Reddit', icon: '\uD83E\uDD16', formats: ['Promoted Post', 'Video', 'Carousel'], dims: '1200x628', charLimit: 300 },
  { id: 'bing', name: 'Bing / Microsoft', icon: '\uD83C\uDF10', formats: ['Search', 'Audience', 'Shopping'], dims: '300x250 / 728x90', charLimit: 90 },
  { id: 'amazon', name: 'Amazon Ads', icon: '\uD83D\uDCE6', formats: ['Sponsored Products', 'Sponsored Brands', 'Display'], dims: '1200x628', charLimit: 150 },
  { id: 'spotify', name: 'Spotify', icon: '\uD83C\uDFB6', formats: ['Audio Ads', 'Video Takeover', 'Sponsored Playlist'], dims: '640x640', charLimit: 75 },
];

const OBJECTIVES = [
  { id: 'awareness', label: 'Brand Awareness', icon: '\uD83D\uDCE2', desc: 'Get your brand in front of the right people' },
  { id: 'traffic', label: 'Website Traffic', icon: '\uD83C\uDF10', desc: 'Drive visitors to your website or landing page' },
  { id: 'leads', label: 'Lead Generation', icon: '\uD83D\uDCCB', desc: 'Capture contact info from interested prospects' },
  { id: 'sales', label: 'Sales / Conversions', icon: '\uD83D\uDCB0', desc: 'Drive purchases and completed transactions' },
  { id: 'app', label: 'App Installs', icon: '\uD83D\uDCF1', desc: 'Promote your mobile app and drive downloads' },
];

const DEMO_CAMPAIGNS = [
  { id: 1, name: 'Spring Launch Campaign', platforms: ['facebook', 'instagram', 'google'], status: 'Active', spend: '$2,450', impressions: '145K', clicks: '3.2K', ctr: '2.2%', created: 'Feb 10, 2026' },
  { id: 2, name: 'LinkedIn B2B Outreach', platforms: ['linkedin'], status: 'Paused', spend: '$890', impressions: '32K', clicks: '890', ctr: '2.8%', created: 'Feb 5, 2026' },
  { id: 3, name: 'TikTok Product Showcase', platforms: ['tiktok', 'instagram'], status: 'Completed', spend: '$1,200', impressions: '280K', clicks: '8.1K', ctr: '2.9%', created: 'Jan 28, 2026' },
];

const STEP_LABELS = ['Platforms', 'Objective', 'Audience', 'Budget', 'AI Generation', 'Preview & Export'];

function AdAgentPage() {
  const { user } = useAuth();
  const [selectedPlatforms, setSelectedPlatforms] = useState([]);
  const [step, setStep] = useState(0);
  const [objective, setObjective] = useState(null);
  const [audience, setAudience] = useState({ ageMin: 25, ageMax: 54, location: '', interests: '', behaviors: '' });
  const [budget, setBudget] = useState({ type: 'daily', amount: '50', startDate: '', endDate: '' });
  const [generating, setGenerating] = useState(false);
  const [generated, setGenerated] = useState(false);
  const [tab, setTab] = useState('builder');
  const [businessInfo] = useState({
    name: 'My Business',
    industry: 'Professional Services',
    website: 'mybusiness.pages.dev',
    tagline: 'Quality you can trust',
    colors: ['#6366f1', '#10b981'],
  });

  const togglePlatform = (id) => {
    setSelectedPlatforms(prev =>
      prev.includes(id) ? prev.filter(p => p !== id) : [...prev, id]
    );
  };

  const handleGenerate = () => {
    setGenerating(true);
    setTimeout(() => { setGenerating(false); setGenerated(true); }, 3000);
  };

  const renderStepIndicator = () => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 24, overflowX: 'auto', paddingBottom: 4 }}>
      {STEP_LABELS.map((label, i) => (
        <React.Fragment key={label}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: i <= step ? 'pointer' : 'default', opacity: i <= step ? 1 : 0.4, flexShrink: 0 }} onClick={() => i <= step && setStep(i)}>
            <div style={{ width: 28, height: 28, borderRadius: '50%', background: i < step ? '#10b981' : i === step ? '#6366f1' : 'var(--border)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 600 }}>
              {i < step ? '\u2713' : i + 1}
            </div>
            <span style={{ fontSize: 13, fontWeight: i === step ? 600 : 400, color: i === step ? 'var(--text)' : 'var(--text-muted)', whiteSpace: 'nowrap' }}>{label}</span>
          </div>
          {i < STEP_LABELS.length - 1 && <div style={{ width: 24, height: 1, background: i < step ? '#10b981' : 'var(--border)', flexShrink: 0 }} />}
        </React.Fragment>
      ))}
    </div>
  );

  const renderPlatformSelect = () => (
    <div>
      <h3 style={{ fontSize: 18, fontWeight: 600, color: 'var(--text)', marginBottom: 4 }}>Select Ad Platforms</h3>
      <p style={{ color: 'var(--text-muted)', fontSize: 14, marginBottom: 16 }}>Choose where to advertise. Each platform gets custom-formatted creative.</p>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 12 }}>
        {PLATFORMS.map(p => {
          const sel = selectedPlatforms.includes(p.id);
          return (
            <div key={p.id} onClick={() => togglePlatform(p.id)} style={{ padding: 16, background: sel ? 'rgba(99,102,241,0.08)' : 'var(--card)', border: sel ? '2px solid #6366f1' : '1px solid var(--border)', borderRadius: 12, cursor: 'pointer' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                <span style={{ fontSize: 24 }}>{p.icon}</span>
                <span style={{ fontSize: 15, fontWeight: 600, color: 'var(--text)' }}>{p.name}</span>
                {sel && <span style={{ marginLeft: 'auto', color: '#6366f1', fontWeight: 700 }}>{'\u2713'}</span>}
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                {p.formats.slice(0, 3).map(f => (
                  <span key={f} style={{ fontSize: 11, padding: '2px 8px', background: 'var(--bg)', borderRadius: 4, color: 'var(--text-muted)' }}>{f}</span>
                ))}
                {p.formats.length > 3 && <span style={{ fontSize: 11, padding: '2px 8px', color: 'var(--text-muted)' }}>+{p.formats.length - 3}</span>}
              </div>
            </div>
          );
        })}
      </div>
      <p style={{ color: 'var(--text-muted)', fontSize: 13, marginTop: 12 }}>{selectedPlatforms.length} platform{selectedPlatforms.length !== 1 ? 's' : ''} selected</p>
    </div>
  );

  const renderObjectiveSelect = () => (
    <div>
      <h3 style={{ fontSize: 18, fontWeight: 600, color: 'var(--text)', marginBottom: 16 }}>Campaign Objective</h3>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 12 }}>
        {OBJECTIVES.map(o => {
          const sel = objective === o.id;
          return (
            <div key={o.id} onClick={() => setObjective(o.id)} style={{ padding: 20, background: sel ? 'rgba(99,102,241,0.08)' : 'var(--card)', border: sel ? '2px solid #6366f1' : '1px solid var(--border)', borderRadius: 12, cursor: 'pointer' }}>
              <div style={{ fontSize: 32, marginBottom: 8 }}>{o.icon}</div>
              <h4 style={{ fontSize: 15, fontWeight: 600, color: 'var(--text)', marginBottom: 4 }}>{o.label}</h4>
              <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>{o.desc}</p>
            </div>
          );
        })}
      </div>
    </div>
  );

  const renderAudienceStep = () => (
    <div>
      <h3 style={{ fontSize: 18, fontWeight: 600, color: 'var(--text)', marginBottom: 16 }}>Target Audience</h3>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
        <div>
          <label style={labelStyle}>Age Range</label>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <input type="number" value={audience.ageMin} onChange={e => setAudience(p => ({ ...p, ageMin: e.target.value }))} style={{ ...inputStyle, width: 80 }} min={13} max={65} />
            <span style={{ color: 'var(--text-muted)' }}>to</span>
            <input type="number" value={audience.ageMax} onChange={e => setAudience(p => ({ ...p, ageMax: e.target.value }))} style={{ ...inputStyle, width: 80 }} min={13} max={65} />
          </div>
        </div>
        <div>
          <label style={labelStyle}>Location</label>
          <input type="text" value={audience.location} onChange={e => setAudience(p => ({ ...p, location: e.target.value }))} placeholder="e.g. Canada, United States, Alberta" style={inputStyle} />
        </div>
      </div>
      <div style={{ marginBottom: 16 }}>
        <label style={labelStyle}>Interests (comma-separated)</label>
        <input type="text" value={audience.interests} onChange={e => setAudience(p => ({ ...p, interests: e.target.value }))} placeholder="e.g. technology, small business, entrepreneurship" style={inputStyle} />
      </div>
      <div>
        <label style={labelStyle}>Behaviors</label>
        <input type="text" value={audience.behaviors} onChange={e => setAudience(p => ({ ...p, behaviors: e.target.value }))} placeholder="e.g. online shoppers, business owners, frequent travelers" style={inputStyle} />
      </div>
    </div>
  );

  const renderBudgetStep = () => (
    <div>
      <h3 style={{ fontSize: 18, fontWeight: 600, color: 'var(--text)', marginBottom: 16 }}>Budget & Schedule</h3>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>
        <div>
          <label style={labelStyle}>Budget Type</label>
          <div style={{ display: 'flex', gap: 8 }}>
            {['daily', 'lifetime'].map(t => (
              <button key={t} onClick={() => setBudget(p => ({ ...p, type: t }))} style={{ flex: 1, padding: '10px 16px', background: budget.type === t ? '#6366f1' : 'var(--card)', color: budget.type === t ? '#fff' : 'var(--text-muted)', border: budget.type === t ? 'none' : '1px solid var(--border)', borderRadius: 8, fontSize: 14, fontWeight: 500, cursor: 'pointer', textTransform: 'capitalize' }}>{t}</button>
            ))}
          </div>
        </div>
        <div>
          <label style={labelStyle}>Amount ($CAD)</label>
          <input type="number" value={budget.amount} onChange={e => setBudget(p => ({ ...p, amount: e.target.value }))} style={inputStyle} min={1} />
        </div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        <div>
          <label style={labelStyle}>Start Date</label>
          <input type="date" value={budget.startDate} onChange={e => setBudget(p => ({ ...p, startDate: e.target.value }))} style={inputStyle} />
        </div>
        <div>
          <label style={labelStyle}>End Date</label>
          <input type="date" value={budget.endDate} onChange={e => setBudget(p => ({ ...p, endDate: e.target.value }))} style={inputStyle} />
        </div>
      </div>
      <div style={{ marginTop: 20, padding: 16, background: 'rgba(99,102,241,0.06)', borderRadius: 12, border: '1px solid rgba(99,102,241,0.2)' }}>
        <h4 style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)', marginBottom: 8 }}>Estimated Reach</h4>
        <div style={{ display: 'flex', gap: 24 }}>
          <div><span style={{ fontSize: 20, fontWeight: 700, color: '#6366f1' }}>12K-35K</span><p style={{ fontSize: 12, color: 'var(--text-muted)' }}>Impressions/day</p></div>
          <div><span style={{ fontSize: 20, fontWeight: 700, color: '#10b981' }}>240-700</span><p style={{ fontSize: 12, color: 'var(--text-muted)' }}>Clicks/day</p></div>
          <div><span style={{ fontSize: 20, fontWeight: 700, color: '#f59e0b' }}>$0.85</span><p style={{ fontSize: 12, color: 'var(--text-muted)' }}>Est. CPC</p></div>
        </div>
      </div>
    </div>
  );

  const renderGenerationStep = () => (
    <div style={{ textAlign: 'center', padding: '40px 0' }}>
      {generating ? (
        <>
          <div style={{ fontSize: 48, marginBottom: 16, animation: 'pulse 1.5s infinite' }}>{'\u2728'}</div>
          <h3 style={{ fontSize: 20, fontWeight: 600, color: 'var(--text)', marginBottom: 8 }}>AI is crafting your ads...</h3>
          <p style={{ color: 'var(--text-muted)', fontSize: 14, marginBottom: 24 }}>Generating custom creative for {selectedPlatforms.length} platform{selectedPlatforms.length !== 1 ? 's' : ''}</p>
          <div style={{ width: 200, height: 4, background: 'var(--border)', borderRadius: 2, margin: '0 auto', overflow: 'hidden' }}>
            <div style={{ width: '60%', height: '100%', background: '#6366f1', borderRadius: 2, animation: 'loading 2s infinite' }} />
          </div>
        </>
      ) : !generated ? (
        <>
          <div style={{ fontSize: 48, marginBottom: 16 }}>{'\uD83E\uDD16'}</div>
          <h3 style={{ fontSize: 20, fontWeight: 600, color: 'var(--text)', marginBottom: 8 }}>Ready to Generate</h3>
          <p style={{ color: 'var(--text-muted)', fontSize: 14, marginBottom: 8 }}>AI will create custom ad copy, headlines, and descriptions for each platform.</p>
          <p style={{ color: 'var(--text-muted)', fontSize: 13, marginBottom: 24 }}>Business info auto-pulled from your Forge Standard profile.</p>
          <div style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 12, padding: 20, maxWidth: 400, margin: '0 auto 24px', textAlign: 'left' }}>
            <h4 style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 10, textTransform: 'uppercase', letterSpacing: 1 }}>Business Profile</h4>
            <p style={{ fontSize: 14, color: 'var(--text)', marginBottom: 4 }}><strong>{businessInfo.name}</strong></p>
            <p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 4 }}>{businessInfo.industry} | {businessInfo.website}</p>
            <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>"{businessInfo.tagline}"</p>
          </div>
          <button onClick={handleGenerate} style={{ padding: '12px 32px', background: 'linear-gradient(135deg, #6366f1, #8b5cf6)', color: '#fff', border: 'none', borderRadius: 10, fontSize: 16, fontWeight: 600, cursor: 'pointer', boxShadow: '0 4px 12px rgba(99,102,241,0.3)' }}>
            {'\u2728'} Generate Ad Package
          </button>
        </>
      ) : (
        <div style={{ textAlign: 'left' }}>
          <div style={{ textAlign: 'center', marginBottom: 24 }}>
            <div style={{ fontSize: 48, marginBottom: 8 }}>{'\u2705'}</div>
            <h3 style={{ fontSize: 20, fontWeight: 600, color: '#10b981' }}>Ad Package Generated!</h3>
          </div>
          {selectedPlatforms.map(pid => {
            const plat = PLATFORMS.find(p => p.id === pid);
            return (
              <div key={pid} style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 12, padding: 20, marginBottom: 12 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                  <span style={{ fontSize: 24 }}>{plat.icon}</span>
                  <h4 style={{ fontSize: 16, fontWeight: 600, color: 'var(--text)' }}>{plat.name}</h4>
                  <span style={{ marginLeft: 'auto', fontSize: 12, padding: '4px 10px', background: 'rgba(16,185,129,0.1)', color: '#10b981', borderRadius: 6, fontWeight: 500 }}>Ready</span>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <div><label style={{ fontSize: 12, color: 'var(--text-muted)', fontWeight: 600 }}>Headline</label><p style={{ fontSize: 14, color: 'var(--text)', marginTop: 2 }}>Transform Your Business Today</p></div>
                  <div><label style={{ fontSize: 12, color: 'var(--text-muted)', fontWeight: 600 }}>Description</label><p style={{ fontSize: 14, color: 'var(--text)', marginTop: 2 }}>Professional solutions tailored to your industry. Get started now.</p></div>
                  <div><label style={{ fontSize: 12, color: 'var(--text-muted)', fontWeight: 600 }}>Dimensions</label><p style={{ fontSize: 14, color: 'var(--text)', marginTop: 2 }}>{plat.dims}</p></div>
                  <div><label style={{ fontSize: 12, color: 'var(--text-muted)', fontWeight: 600 }}>UTM Link</label><p style={{ fontSize: 13, color: '#6366f1', marginTop: 2 }}>{businessInfo.website}?utm_source={pid}&utm_medium=cpc&utm_campaign=spring2026</p></div>
                </div>
                <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                  <button style={{ padding: '6px 14px', background: '#6366f1', color: '#fff', border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 500, cursor: 'pointer' }}>Download Assets</button>
                  <button style={{ padding: '6px 14px', background: 'transparent', color: '#6366f1', border: '1px solid #6366f1', borderRadius: 6, fontSize: 13, fontWeight: 500, cursor: 'pointer' }}>Edit Copy</button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );

  const renderCampaignHistory = () => (
    <div style={{ marginTop: 32 }}>
      <h2 style={{ fontSize: 20, fontWeight: 600, color: 'var(--text)', marginBottom: 16 }}>Campaign History</h2>
      <div style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 12, overflow: 'hidden' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1.5fr 100px 100px 100px 80px 80px 120px', padding: '12px 20px', background: 'var(--bg)', borderBottom: '1px solid var(--border)', fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.5 }}>
          <span>Campaign</span><span>Platforms</span><span>Status</span><span>Spend</span><span>Impressions</span><span>Clicks</span><span>CTR</span><span>Created</span>
        </div>
        {DEMO_CAMPAIGNS.map(c => (
          <div key={c.id} style={{ display: 'grid', gridTemplateColumns: '2fr 1.5fr 100px 100px 100px 80px 80px 120px', padding: '14px 20px', borderBottom: '1px solid var(--border)', alignItems: 'center', fontSize: 14 }}>
            <span style={{ fontWeight: 500, color: 'var(--text)' }}>{c.name}</span>
            <div style={{ display: 'flex', gap: 4 }}>
              {c.platforms.map(pid => {
                const pl = PLATFORMS.find(p => p.id === pid);
                return <span key={pid} title={pl?.name} style={{ fontSize: 16 }}>{pl?.icon}</span>;
              })}
            </div>
            <span style={{ fontSize: 13, fontWeight: 500, color: c.status === 'Active' ? '#10b981' : c.status === 'Paused' ? '#f59e0b' : 'var(--text-muted)' }}>{c.status}</span>
            <span style={{ color: 'var(--text-muted)', fontSize: 13 }}>{c.spend}</span>
            <span style={{ color: 'var(--text-muted)', fontSize: 13 }}>{c.impressions}</span>
            <span style={{ color: 'var(--text-muted)', fontSize: 13 }}>{c.clicks}</span>
            <span style={{ color: 'var(--text-muted)', fontSize: 13 }}>{c.ctr}</span>
            <span style={{ color: 'var(--text-muted)', fontSize: 13 }}>{c.created}</span>
          </div>
        ))}
      </div>
    </div>
  );

  const renderAdSpecs = () => (
    <div style={{ marginTop: 32 }}>
      <h2 style={{ fontSize: 20, fontWeight: 600, color: 'var(--text)', marginBottom: 16 }}>Ad Format Specifications</h2>
      <div style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 12, overflow: 'hidden' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1.5fr 1.5fr 1.5fr 100px', padding: '12px 20px', background: 'var(--bg)', borderBottom: '1px solid var(--border)', fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase' }}>
          <span>Platform</span><span>Formats</span><span>Dimensions</span><span>Char Limit</span>
        </div>
        {PLATFORMS.map(p => (
          <div key={p.id} style={{ display: 'grid', gridTemplateColumns: '1.5fr 1.5fr 1.5fr 100px', padding: '12px 20px', borderBottom: '1px solid var(--border)', alignItems: 'center', fontSize: 13 }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 500, color: 'var(--text)' }}><span>{p.icon}</span>{p.name}</span>
            <span style={{ color: 'var(--text-muted)' }}>{p.formats.join(', ')}</span>
            <span style={{ color: 'var(--text-muted)', fontFamily: 'monospace', fontSize: 12 }}>{p.dims}</span>
            <span style={{ color: 'var(--text-muted)' }}>{p.charLimit}</span>
          </div>
        ))}
      </div>
    </div>
  );

  const renderStep = () => {
    switch (step) {
      case 0: return renderPlatformSelect();
      case 1: return renderObjectiveSelect();
      case 2: return renderAudienceStep();
      case 3: return renderBudgetStep();
      case 4: case 5: return renderGenerationStep();
      default: return null;
    }
  };

  return (
    <div style={{ padding: '24px 32px', maxWidth: 1200, margin: '0 auto' }}>
      <div style={{ marginBottom: 28 }}>
        <h1 style={{ fontSize: 28, fontWeight: 700, color: 'var(--text)', marginBottom: 6 }}>{'\uD83D\uDCE3'} Ad Agent</h1>
        <p style={{ color: 'var(--text-muted)', fontSize: 15, maxWidth: 600 }}>AI-powered advertising across every major platform. Custom creative, auto-generated from your business profile.</p>
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 24 }}>
        {['builder', 'history', 'specs'].map(t => (
          <button key={t} onClick={() => setTab(t)} style={{ padding: '8px 20px', background: tab === t ? '#6366f1' : 'var(--card)', color: tab === t ? '#fff' : 'var(--text-muted)', border: tab === t ? 'none' : '1px solid var(--border)', borderRadius: 8, fontSize: 14, fontWeight: 500, cursor: 'pointer', textTransform: 'capitalize' }}>{t === 'specs' ? 'Ad Specs' : t}</button>
        ))}
      </div>

      {tab === 'builder' && (
        <div style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 16, padding: 28 }}>
          {renderStepIndicator()}
          {renderStep()}
          {step > 0 && step < 4 && (
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 24 }}>
              <button onClick={() => setStep(s => s - 1)} style={{ padding: '10px 24px', background: 'transparent', color: 'var(--text-muted)', border: '1px solid var(--border)', borderRadius: 8, fontSize: 14, cursor: 'pointer' }}>{'\u2190'} Back</button>
              <button onClick={() => setStep(s => s + 1)} style={{ padding: '10px 24px', background: '#6366f1', color: '#fff', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 500, cursor: 'pointer' }}>Next {'\u2192'}</button>
            </div>
          )}
          {step === 0 && selectedPlatforms.length > 0 && (
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 24 }}>
              <button onClick={() => setStep(1)} style={{ padding: '10px 24px', background: '#6366f1', color: '#fff', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 500, cursor: 'pointer' }}>Next {'\u2192'}</button>
            </div>
          )}
        </div>
      )}

      {tab === 'history' && renderCampaignHistory()}
      {tab === 'specs' && renderAdSpecs()}
    </div>
  );
}

const labelStyle = { display: 'block', fontSize: 13, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 6 };
const inputStyle = { width: '100%', padding: '10px 14px', background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 8, color: 'var(--text)', fontSize: 14, outline: 'none', boxSizing: 'border-box' };

export default AdAgentPage;
